import type { Address, Hex } from 'viem'

const STORAGE_KEY = 'dora:web3-deposit:pending:v1'
const NOTIFICATION_STORAGE_PREFIX = 'dora:web3-deposit:notification:v1'

export type DepositNotificationOutcome =
  | 'credited'
  | 'verification-failed'
  | 'orphaned'
  | 'user-not-exist'
  | 'dust'
  | 'reverted'

export type PendingDepositRecord = {
  version: 1
  userId: string
  walletAddress: Address
  chainId: number
  quantity: string
  phase: 'instructions' | 'transaction-submitted' | 'credit-pending'
  txHash?: Hex
  createdAt: string
}

const isPendingDepositRecord = (value: unknown): value is PendingDepositRecord => {
  if (!value || typeof value !== 'object') return false
  const record = value as Partial<PendingDepositRecord>
  return (
    record.version === 1 &&
    typeof record.userId === 'string' &&
    /^0x[0-9a-f]{40}$/i.test(record.walletAddress ?? '') &&
    Number.isSafeInteger(record.chainId) &&
    typeof record.quantity === 'string' &&
    ['instructions', 'transaction-submitted', 'credit-pending'].includes(record.phase ?? '') &&
    typeof record.createdAt === 'string' &&
    (!record.txHash || /^0x[0-9a-f]{64}$/i.test(record.txHash))
  )
}

type DepositStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

export const loadPendingDeposits = (
  storage: DepositStorage = window.sessionStorage,
): PendingDepositRecord[] => {
  try {
    const raw = storage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    // Accept the single-record format saved by earlier versions.
    const records = Array.isArray(parsed) ? parsed : [parsed]
    return records.filter(isPendingDepositRecord).filter((record) => record.txHash)
  } catch {
    return []
  }
}

export const savePendingDeposit = (
  record: PendingDepositRecord,
  storage: DepositStorage = window.sessionStorage,
) => {
  // Resume tracking only after broadcast; an unsigned draft has no transaction to reconcile.
  if (!record.txHash) return
  const records = loadPendingDeposits(storage).filter(
    (pending) => pending.txHash?.toLowerCase() !== record.txHash?.toLowerCase(),
  )
  storage.setItem(STORAGE_KEY, JSON.stringify([...records, record]))
}

export const clearPendingDeposit = (
  txHash: Hex,
  storage: DepositStorage = window.sessionStorage,
) => {
  const records = loadPendingDeposits(storage).filter(
    (record) => record.txHash?.toLowerCase() !== txHash.toLowerCase(),
  )
  if (records.length) storage.setItem(STORAGE_KEY, JSON.stringify(records))
  else storage.removeItem(STORAGE_KEY)
}

export const claimDepositNotification = (
  txHash: Hex,
  outcome: DepositNotificationOutcome,
  storage: Pick<Storage, 'getItem' | 'setItem'> = window.sessionStorage,
) => {
  // Deduplicate each outcome independently so a later outcome can still notify for the same tx.
  const key = `${NOTIFICATION_STORAGE_PREFIX}:${txHash.toLowerCase()}:${outcome}`
  try {
    if (storage.getItem(key)) return false
    storage.setItem(key, '1')
    return true
  } catch {
    return true
  }
}
